import LucideIcon from "@components/Atoms/Icon/LucideIcon";
import { useRouter } from "@core/Api/useRouter";
import type { PageError, UnreadableArticle } from "@core/SitePresenter/types/ArticlePage";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { cn } from "@core-ui/utils/cn";
import { defaultRefreshPage } from "@core-ui/utils/initGlobalFuncs";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { Collapsible, CollapsibleButtonTrigger, CollapsibleContent } from "@ui-kit/Collapsible";
import {
	PageState,
	PageStateButtonGroup,
	PageStateDescription,
	PageStateFeatureIcon,
	PageStateTitle,
} from "@ui-kit/PageState";
import { type ComponentProps, type ReactNode, useState } from "react";

/** Renders `{{name}}` placeholders as code, keeping the sentence one translatable string. */
const withCode = (template: string, values: Record<string, string>) =>
	template.split(/\{\{(\w+)\}\}/).map((part, index) =>
		index % 2 ? (
			// biome-ignore lint/suspicious/noArrayIndexKey: split output is positional by nature
			<code className="rounded bg-primary-bg px-1" key={index}>
				{values[part]}
			</code>
		) : (
			part
		),
	);

/** The file around the failure. The line is marked by contrast, not colour: the claim is «look here». */
const Source = ({ source, line }: { source: UnreadableArticle["source"]; line: number }) => {
	const width = String(source[source.length - 1].number).length;

	return (
		<pre className="mt-2 max-w-full overflow-x-auto rounded-md bg-secondary-bg p-3 text-left text-xs leading-relaxed">
			{source.map((sourceLine) => {
				const isFailing = sourceLine.number === line;
				return (
					<div
						className={cn(
							"flex gap-3 whitespace-pre px-1",
							isFailing && "rounded-sm bg-primary-bg font-medium text-primary-fg",
						)}
						key={sourceLine.number}
					>
						<span className={cn("select-none", isFailing ? "text-primary-fg" : "text-muted")}>
							{String(sourceLine.number).padStart(width)}
						</span>
						<span>{sourceLine.text || " "}</span>
					</div>
				);
			})}
		</pre>
	);
};

/** What is copied into a bug report. Folded and labelled as `board/standards/alert-dialogs.md` requires. */
const Details = ({ unreadable }: { unreadable: UnreadableArticle }) => {
	const [open, setOpen] = useState(false);
	const { line, path, source, mismatch, reason } = unreadable;
	if (!source && !reason) return null;

	// Markup the parser could not recognise falls through to ordinary text, hence `paragraph`.
	const isPlainText = mismatch?.got === "paragraph" || mismatch?.got === "text";

	return (
		// `PageState` sizes children to their content, so `overflow-x` needs the width pinned first.
		<Collapsible className="mt-3 w-full min-w-0 max-w-3xl text-left" onOpenChange={setOpen} open={open}>
			<CollapsibleButtonTrigger open={open}>{t("technical-details")}</CollapsibleButtonTrigger>
			<CollapsibleContent>
				{path && (
					<div className="mt-2 text-xs text-muted">
						{t("article.unreadable.file").replace("{{path}}", path)}
					</div>
				)}
				{source && <Source line={line} source={source} />}
				<div className="mt-3 space-y-2 text-xs text-muted">
					{mismatch && (
						<div>
							{withCode(
								t(isPlainText ? "article.unreadable.mismatch-text" : "article.unreadable.mismatch"),
								{
									node: mismatch.node,
									expected: mismatch.expected.join(", "),
									got: mismatch.got,
								},
							)}
							{/* The schema breaks at the first thing that does not fit, which can sit far below
							    the tag that never closed — so where the construct opened is often where the
							    mistake actually is. */}
							{mismatch.nodeLine && mismatch.nodeLine !== line
								? ` ${t("article.unreadable.opened-at").replace("{{line}}", String(mismatch.nodeLine))}`
								: ""}
						</div>
					)}
					{reason && (
						<div>
							{t("article.unreadable.reason")} <span className="font-mono">{reason}</span>
						</div>
					)}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
};

/** The unknown failure keeps a fold too — its own words are all it has to offer. */
const UnknownDetails = ({ path, reason }: { path?: string; reason?: string }) => {
	const [open, setOpen] = useState(false);
	if (!reason) return null;

	return (
		<Collapsible className="mt-3 w-full min-w-0 max-w-3xl text-left" onOpenChange={setOpen} open={open}>
			<CollapsibleButtonTrigger open={open}>{t("technical-details")}</CollapsibleButtonTrigger>
			<CollapsibleContent>
				{path && (
					<div className="mt-2 text-xs text-muted">
						{t("article.unreadable.file").replace("{{path}}", path)}
					</div>
				)}
				<pre className="mt-2 max-w-full overflow-x-auto rounded-md bg-secondary-bg p-3 text-left text-xs">
					{reason}
				</pre>
			</CollapsibleContent>
		</Collapsible>
	);
};

type PageText = { title: string; diagnosis: ReactNode; remedy: ReactNode };

/** Diagnosis and remedy as separate paragraphs; values the reader must carry are set as code. */
const useText = (error: PageError, canRepair: boolean, catalogTitle?: string): PageText => {
	if (error.kind === "article-not-found") {
		// A catalog with no title of its own is left out rather than named as empty quotes.
		const template = catalogTitle
			? t("article.page-error.article-not-found.desc").replace("{{catalog}}", catalogTitle)
			: t("article.page-error.article-not-found.desc-no-catalog");

		return {
			title: t("article.page-error.article-not-found.title"),
			diagnosis: withCode(template, { path: error.path }),
			remedy: t("article.page-error.article-not-found.remedy"),
		};
	}

	if (error.kind === "catalog-not-found")
		return {
			title: t("article.page-error.catalog-not-found.title"),
			// Nothing to quote back when the request named no catalog at all.
			diagnosis: error.path
				? withCode(t("article.page-error.catalog-not-found.desc"), { path: error.path })
				: t("article.page-error.catalog-not-found.desc-no-path"),
			remedy: t("article.page-error.catalog-not-found.remedy"),
		};

	if (error.kind === "forbidden")
		return {
			title: t("article.page-error.forbidden.title"),
			diagnosis: t("article.page-error.forbidden.desc"),
			remedy: t("article.page-error.forbidden.remedy"),
		};

	if (error.kind === "unknown")
		return {
			title: t("article.page-error.unknown.title"),
			diagnosis: t("article.page-error.unknown.desc"),
			remedy: t("article.page-error.unknown.remedy"),
		};

	// Which half of the file broke means something only to whoever can open the source.
	if (!canRepair)
		return {
			title: t("article.page-error.read-only.title"),
			diagnosis: t("article.page-error.read-only.desc"),
			remedy: t("article.page-error.read-only.remedy"),
		};

	if (error.kind === "properties")
		return {
			title: t("article.unreadable.properties.title"),
			diagnosis: t("article.unreadable.properties.desc"),
			remedy: t("article.unreadable.properties.remedy"),
		};

	return {
		title: t("article.unreadable.markup.title"),
		diagnosis: error.line
			? withCode(t("article.unreadable.markup.at-line"), { line: String(error.line) })
			: t("article.unreadable.markup.somewhere"),
		remedy: t("article.unreadable.markup.remedy"),
	};
};

/** Where the page sends the reader. A button is offered only when there is somewhere to go. */
const useActions = (error: PageError, canRepair: boolean): ReactNode => {
	const router = useRouter();
	const { isWeb } = usePlatform();
	const { isLogged } = PageDataContextService.value;
	const apiUrlCreator = ApiUrlCreatorService.value;
	const catalogPathname = useCatalogPropsStore((state) => state.data?.link?.pathname);

	if (error.kind === "article-not-found") {
		const buttons = [
			catalogPathname && (
				<Button key="home" onClick={() => void router.pushPath(`/${catalogPathname}`)} variant="primary">
					{t("article.page-error.article-not-found.action")}
				</Button>
			),
			// Only in the browser: elsewhere the reader is already in the desktop app.
			isWeb && error.path && (
				<Button
					key="desktop"
					onClick={() => {
						window.location.href = `gramax://${error.path}`;
					}}
					variant="outline"
				>
					{t("article.page-error.article-not-found.open-in-desktop")}
				</Button>
			),
		].filter(Boolean);

		// An empty fragment is still truthy, and would leave the page with an empty button group.
		return buttons.length ? buttons : null;
	}

	if (error.kind === "catalog-not-found")
		return (
			<Button onClick={() => void router.pushPath("/")} variant="primary">
				{t("article.page-error.catalog-not-found.action")}
			</Button>
		);

	if (error.kind === "forbidden")
		return isLogged ? null : (
			<Button
				onClick={() => {
					window.location.href = apiUrlCreator.getAuthUrl(router, false).toString();
				}}
				variant="primary"
			>
				{t("article.page-error.forbidden.action")}
			</Button>
		);

	if (error.kind === "unknown")
		return (
			<Button onClick={() => defaultRefreshPage()} variant="primary">
				{t("article.page-error.unknown.action")}
			</Button>
		);

	// Properties are always placed — they are the top of the file. Markup only when the parser said where.
	if (!canRepair || (error.kind === "markup" && !error.line)) return null;

	return (
		<Button
			onClick={() =>
				router.pushQuery({
					...(router.query || {}),
					mode: "markdown",
					...(error.line && { line: String(error.line) }),
				})
			}
			startIcon="markdown"
			variant="primary"
		>
			{t("article.unreadable.open-markup")}
		</Button>
	);
};

/** One page for every reason there is no article to show. */
const ArticlePageError = ({ error }: { error: PageError }) => {
	// A reader who cannot edit here cannot repair the article either.
	const canRepair = !PageDataContextService.value.conf.isReadOnly;
	const catalogTitle = useCatalogPropsStore((state) => state.data?.title);
	const text = useText(error, canRepair, catalogTitle);
	const actions = useActions(error, canRepair);

	return (
		<PageState className="my-8">
			{/* `PageStateFeatureIcon` sizes the icon from the page-state context; the icon set is
			    resolved by name, and its custom-icon branch is wider than the type here allows. */}
			<PageStateFeatureIcon
				icon={LucideIcon("circle-alert") as ComponentProps<typeof PageStateFeatureIcon>["icon"]}
			/>
			<PageStateTitle className="text-center">{text.title}</PageStateTitle>
			<PageStateDescription>{text.diagnosis}</PageStateDescription>
			<PageStateDescription>{text.remedy}</PageStateDescription>
			{actions && <PageStateButtonGroup>{actions}</PageStateButtonGroup>}
			{canRepair && (error.kind === "markup" || error.kind === "properties") && <Details unreadable={error} />}
			{error.kind === "unknown" && <UnknownDetails path={error.path} reason={error.reason} />}
		</PageState>
	);
};

export default ArticlePageError;
